import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import MessageList from "./MessageList";
import Composer from "./Composer";
import LandingView from "./LandingView";
import HistoryDrawer from "./HistoryDrawer";
import DottedBackground from "./DottedBackground";
import ActionCard from "./ActionCard";
import ResultsPanel from "./ResultsPanel";
import { LimitTooltip } from "./LimitTip";
import { ConversationSkeleton, SkeletonBar } from "./Skeleton";
import { ActionPanelContext, requestedAction } from "@containers/Hermes/hooks/useActionPanel";
import type { ActionTrigger, HermesAction, ResultsSource } from "@containers/Hermes/hooks/useActionPanel";
import useHermesAnalytics from "@containers/Hermes/hooks/useHermesAnalytics";
import { selectIsActiveStreaming, selectIsAwaiting } from "@redux/Hermes/hermesSlice";
import type { AbortStream, HermesRootState, SendMessage } from "@containers/Hermes/types";

type ChatShellProps = {
  sendMessage: SendMessage;
  abort: AbortStream;
  onNewChat: () => void;
  onSelectConversation: (id: string) => void;
};

export default function ChatShell({ sendMessage, abort, onNewChat, onSelectConversation }: ChatShellProps) {
  const activeConversationId = useSelector((state: HermesRootState) => state.hermes.activeConversationId);
  const messages = useSelector((state: HermesRootState) => state.hermes.messages);
  const conversations = useSelector((state: HermesRootState) => state.hermes.conversations);
  const knownTitle = conversations.find((c) => c.id === activeConversationId)?.title;
  const title = knownTitle || "New conversation";

  // Scope "streaming" to the conversation in view — a stream running in the
  // background for another conversation must not lock this view's composer.
  const isStreaming = useSelector(selectIsActiveStreaming);
  // A response generating in the background (polling) for the conversation in
  // view. It also locks the composer, but has no live stream to Stop.
  const isAwaiting = useSelector(selectIsAwaiting);
  // A conversation being opened has no messages yet; it shows its own skeleton rather than the welcome view.
  const isLoadingConversation = useSelector((state: HermesRootState) => state.hermes.status === "loading" && state.hermes.loadingConversationId !== null);
  const isEmpty = messages.length === 0 && !isStreaming && !isAwaiting && !isLoadingConversation;

  // Single send path for the composer, landing suggestions, and follow-up
  // chips — always targets the active conversation.
  const handleSend = useCallback((text: string, confirmToken?: string) => sendMessage(text, activeConversationId, confirmToken), [sendMessage, activeConversationId]);

  const analytics = useHermesAnalytics();

  // The action card (bookmark / contacts / download) opened from a message's
  // action bar. While open it takes the composer's place; each open mounts it
  // afresh (keyed by openCount) so it starts from the offer's defaults.
  const [action, setAction] = useState<{ action: HermesAction; openCount: number } | null>(null);
  const openAction = useCallback(
    (next: HermesAction, trigger: ActionTrigger = "action_bar") => {
      // The cost confirmation is not one of the tracked actions.
      if (next.kind !== "credit") analytics.actionOpened({ action: next.kind, trigger });
      setAction((previous) => ({ action: next, openCount: (previous?.openCount ?? 0) + 1 }));
    },
    [analytics]
  );
  const closeAction = useCallback(() => setAction(null), []);

  // The full table, opened on the right when the user chooses to customise the
  // selection from all the records. Replaces the action card; closing it closes
  // the action.
  const [panel, setPanel] = useState<{ source: ResultsSource; openCount: number } | null>(null);
  const openPanel = useCallback((next: ResultsSource) => {
    setAction(null);
    setPanel((previous) => ({ source: next, openCount: (previous?.openCount ?? 0) + 1 }));
  }, []);
  const closePanel = useCallback(() => setPanel(null), []);
  const actionContext = useMemo(() => ({ available: true, isOpen: action !== null || panel !== null, open: openAction, openResults: openPanel }), [action, panel, openAction, openPanel]);

  // A card opened for one conversation's answer must not carry over to another.
  useEffect(() => {
    setAction(null);
    setPanel(null);
  }, [activeConversationId]);

  // When an answer the user just asked for arrives with an action marked to
  // open ("contacts for Samsung" → the contacts card, that company selected),
  // open its card once. Only for a turn that finished in this view: never on
  // reload, and not when the view switched conversations mid-answer.
  const turnRef = useRef<{ busy: boolean; conversationId: string | null; pending: boolean }>({ busy: false, conversationId: null, pending: false });
  useEffect(() => {
    const busy = isStreaming || isAwaiting;
    const turn = turnRef.current;
    if (busy && !turn.busy) turn.conversationId = activeConversationId;
    // A brand-new conversation gets its id mid-turn, so a turn started with none still counts.
    if (!busy && turn.busy && (turn.conversationId === null || turn.conversationId === activeConversationId)) turn.pending = true;
    turn.busy = busy;
    if (busy || !turn.pending) return;

    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant" || last.id <= 0) return;
    turn.pending = false;
    const requested = requestedAction(last, activeConversationId);
    if (requested) openAction(requested, "auto_open");
  }, [isStreaming, isAwaiting, messages, activeConversationId, openAction]);

  const [historyOpen, setHistoryOpen] = useState(false);
  const closeHistory = useCallback(() => setHistoryOpen(false), []);

  // Landing state — no conversation open: just the welcome view.
  if (isEmpty) {
    return (
      <div className="tw-relative tw-flex tw-h-[calc(100vh-3rem)] tw-overflow-hidden">
        <div className="tw-flex-grow tw-min-w-0 tw-h-full">
          <LandingView isStreaming={isStreaming} onSend={handleSend} onStop={abort} onOpenHistory={() => setHistoryOpen(true)} />
        </div>
        <HistoryDrawer open={historyOpen} onClose={closeHistory} onNewChat={onNewChat} onSelectConversation={onSelectConversation} />
      </div>
    );
  }

  // Conversation state — header with chat history / new chat, then the messages.
  return (
    <div className="tw-relative tw-flex tw-h-[calc(100vh-3rem)] tw-overflow-hidden">
      <DottedBackground />
      <LimitTooltip />
      <div className="tw-relative tw-flex tw-flex-col tw-flex-grow tw-min-w-0">
        <div className="tw-flex-shrink-0 tw-w-full tw-max-w-[790px] tw-mx-auto tw-box-border tw-pt-4">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-h-8">
            {isLoadingConversation && !knownTitle ? (
              <SkeletonBar className="tw-h-5 tw-w-56" />
            ) : (
              <span title={title} className="tw-min-w-0 tw-truncate tw-text-base tw-font-medium tw-leading-6 tw-tracking-[-0.08px] tw-text-gray-900">
                {title}
              </span>
            )}
            <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-2">
              <button onClick={() => setHistoryOpen(true)} className="tw-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded-lg tw-border-none tw-bg-transparent hover:tw-bg-gray-50 tw-cursor-pointer tw-text-sm tw-font-medium tw-leading-5 tw-tracking-[-0.07px] tw-text-gray-700 hover:tw-text-gray-900 tw-transition-colors">
                <span className="material-symbols-outlined !tw-text-[18px]">history</span>
                Chat history
              </button>
              <button onClick={onNewChat} className="tw-flex tw-items-center tw-gap-1.5 tw-h-8 tw-px-3 tw-rounded-lg tw-border-none tw-bg-transparent hover:tw-bg-gray-50 tw-cursor-pointer tw-text-sm tw-font-medium tw-leading-5 tw-tracking-[-0.07px] tw-text-primary-purple-500 hover:tw-text-primary-purple-600 tw-transition-colors">
                <span className="material-symbols-outlined !tw-text-[18px]">add</span>
                New chat
              </button>
            </div>
          </div>
          <div className="tw-mt-[13px] tw-h-px tw-bg-gray-200" />
        </div>

        <div className="tw-flex-grow tw-min-h-0 tw-relative">
          {isLoadingConversation && messages.length === 0 ? (
            <ConversationSkeleton />
          ) : (
            <ActionPanelContext.Provider value={actionContext}>
              <MessageList onFollowUp={handleSend} />
            </ActionPanelContext.Provider>
          )}
          {/* Soft edges so messages fade under the header and above the composer
              instead of being cut off mid-line. */}
          <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-top-0 tw-h-4 tw-bg-gradient-to-b tw-from-white tw-to-transparent" />
          <div aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-6 tw-bg-gradient-to-t tw-from-white tw-to-transparent" />
        </div>

        <div className="tw-flex-shrink-0 tw-w-full tw-max-w-[790px] tw-mx-auto tw-box-border tw-pb-14 tw-pt-2">
          {action ? <ActionCard key={action.openCount} action={action.action} onClose={closeAction} onCustomise={openPanel} onSend={handleSend} /> : null}
          {/* Hidden rather than unmounted while an action card is open, so
              anything typed in it survives. */}
          <div className={(action && !action.action.fromSelection) || panel ? "tw-hidden" : undefined}>
            <Composer isStreaming={isStreaming} isAwaiting={isAwaiting} onSend={handleSend} onStop={abort} placeholder="Ask a follow up" />
          </div>
        </div>
      </div>

      {panel ? <ResultsPanel key={panel.openCount} source={panel.source} conversationId={activeConversationId} onClose={closePanel} /> : null}

      <HistoryDrawer open={historyOpen} onClose={closeHistory} onNewChat={onNewChat} onSelectConversation={onSelectConversation} />
    </div>
  );
}
